/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import coverImage20260301 from '@app/images/whats-new/2026-03-01.jpg';

export interface WhatsNewEntry {
	id: string;
	date: Date;
	coverImage: string;
	content: string;
}

const MARCH_2026_CONTENT = `# A message from the Fluxer team

First of all – **thank you**. Thank you for trying Fluxer, for building communities here, and for sticking with us through what has been a rocky few weeks. We owe you an honest update.

## What a ride!

Fluxer grew from a small project to **over 120,000 users in just two weeks**. We did not anticipate this kind of growth and, frankly, we were not ready for it. Until very recently, the entire platform was running on a single server – because that was all we ever needed. When traffic exploded, so did our infrastructure.

We're a tiny team. I recently made our first hire, so there are now two of us working on Fluxer full-time. Expecting flawless uptime from a two-person operation serving this many users is setting the bar high, but that does not mean we're not going to try.

## We're doing stuff!

### Infrastructure upgrade
We've moved from a single server to a properly redundant setup with more resources, better failover, and room to grow. The days of one machine going down and taking the whole platform with it are behind us.

### New voice servers
You can now enjoy high quality voice and video on Fluxer with lower latency across the globe, thanks to our expanded server selection:
- 🇦🇺 **Sydney** (Australia)
- 🇧🇷 **São Paulo** (Brazil)
- 🇨🇱 **Santiago** (Chile)
- 🇩🇪 **Frankfurt** (Germany)
- 🇩🇪 **Nuremberg** (Germany)
- 🇪🇸 **Madrid** (Spain)
- 🇸🇪 **Stockholm** (Sweden)
- 🇵🇱 **Warsaw** (Poland)
- 🇮🇳 **Mumbai** (India)
- 🇯🇵 **Tokyo** (Japan)
- 🇸🇬 **Singapore**
- 🇰🇷 **Seoul** (South Korea)
- 🇿🇦 **Johannesburg** (South Africa)
- 🇺🇸 **Miami** (Florida)
- 🇺🇸 **Ashburn** (Virginia)
- 🇺🇸 **Dallas** (Texas)
- 🇺🇸 **Hillsboro** (Oregon)
- 🇺🇸 **Los Angeles** (California)

### Anti-spam and raid protection
We've added a number of new internal safeguards and systems to detect and prevent spam and raids automatically. We're also working on expanding the trust & safety team to scale our moderation capabilities further.

## Plutonium for our early users

Every user who was affected by the server issues over the past few weeks has received **one free week of Plutonium**. If you were already a Plutonium subscriber, you've instead received **three gift codes** (one week of Plutonium each) to give to friends or redeem yourself. Check your gift inventory!

You can read more about this offer and check whether you're eligible in our [help centre article here](https://fluxer.app/help/plutonium-promotion-march-2026).

## Self-hosting (and federation)

This remains a core focus for Fluxer. The documentation is in progress, and we're aiming to have everything in place very soon. PRs are opening up soon!

## Screensharing issues

We're aware that many of you are having issues with screensharing. We'll be shipping new desktop builds as soon as possible to address this, and most fixes are already in place.

Fluxer is still a public beta, so we hope you can forgive the rough edges, especially given the size of the team and the need to keep the old production server from melting down 24/7.

## We're hiring (selectively)

We keep a very small team and are currently limiting new hires to the roles we need most. Our primary focus right now is **part-time trust & safety analysts** and **support representatives**.

We also welcome exceptional candidates – especially experienced professionals who believe in the mission and are willing to start at a lower salary initially. Part-time, per-project, and contractor arrangements are all on the table.

If any of this sounds like you, send an email to <careers@fluxer.app>. Every application is read personally, and even if we can't hire right now, we'll keep your details on file for when we can.

We're aiming to improve the quality of the service now that the infrastructure has been stabilised and the team is growing. We know we have a lot of work ahead of us, but we're committed to making Fluxer the best it can be.

Thank you for being here and for making Fluxer a great place. We couldn't do this without you, and we're excited for what's to come.

**– Hampus, Founder & CEO** :flag_se:`;

export const WHATS_NEW_ENTRIES: ReadonlyArray<WhatsNewEntry> = [
	{
		id: '2026-03-01-infrastructure-update',
		date: new Date('2026-03-01T00:00:00Z'),
		coverImage: coverImage20260301,
		content: MARCH_2026_CONTENT,
	},
];
